import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { collectionPath } from '../../utils/firestoreCollections';
import { aiSummaryStatusMessage } from '../../utils/aiSummaryStatus';
import {
  FEATURE_PERMISSIONS,
  canModifyData,
  canViewAllData,
} from '../../utils/permissions';
import {
  HEALTH_BAND_META, OPEN_STATUSES, sortRecommendations, effectiveStatus, refreshCustomerRecommendations,
  recommendationEvidenceHref
} from '../../utils/recommendations';
import { useRecommendationActions } from '../../hooks/useRecommendationActions';
import RecommendationCard from './RecommendationCard';
import Tooltip from '../common/Tooltip';
import DecisionBrief from './DecisionBrief';
import { buildDecisionBrief } from '../../utils/decisionBrief';

const PANEL_PREVIEW = 10;

const money = (n) => (typeof n === 'number' ? `$${Math.round(n).toLocaleString('en-US')}` : '—');
const dateOnly = (iso) => (iso ? String(iso).split('T')[0] : '—');

const Stat = ({ label, value, hint }) => (
  <div className="min-w-0">
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm font-semibold text-gray-900 break-words">{value}</div>
    {hint && <div className="text-xs text-gray-500 break-words">{hint}</div>}
  </div>
);

const EvidenceReferences = ({ refs }) => {
  if (!Array.isArray(refs) || refs.length === 0) return null;
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-1 text-xs text-gray-500">
      {refs.map((ref, index) => {
        const href = recommendationEvidenceHref(ref);
        const label = `${ref.kind}: ${ref.refId}`;
        return href
          ? <Link key={`${label}-${index}`} to={href} className="text-indigo-600 hover:underline">{label}</Link>
          : <span key={`${label}-${index}`}>{label}</span>;
      })}
    </span>
  );
};

const RecommendationsPanel = ({ customerId, currentUser, users, can, onViewTab, onToast }) => {
  const [insight, setInsight] = useState(null);
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [insightError, setInsightError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAllOpen, setShowAllOpen] = useState(false);
  const [briefEnabled, setBriefEnabled] = useState(false);
  const [briefConfigError, setBriefConfigError] = useState('');
  const [briefClock, setBriefClock] = useState(0);

  const canAct = canModifyData(currentUser) && can(FEATURE_PERMISSIONS.AI_RECS_ACT);
  const canGenerate = canModifyData(currentUser) && can(FEATURE_PERMISSIONS.AI_RECS_GENERATE);
  const canReadTeam = can(FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM) || canViewAllData(currentUser);
  const repNames = useMemo(() => [...new Set([
    currentUser?.displayName,
    currentUser?.full_name,
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName} ${currentUser.lastName}`
      : null,
  ].filter(name => typeof name === 'string' && name.trim()).map(name => name.trim()))], [
    currentUser?.displayName,
    currentUser?.full_name,
    currentUser?.firstName,
    currentUser?.lastName,
  ]);
  const { busyId, handlers, modals } = useRecommendationActions({
    user: currentUser,
    users,
    canAssignAny: can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY),
    onToast
  });

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, collectionPath('config'), 'aiAgentSettings'),
      snap => { setBriefEnabled(snap.data()?.decisionBriefEnabled === true); setBriefConfigError(''); },
      err => {
        console.error('Failed to load decision brief settings:', err);
        setBriefEnabled(false);
        setBriefConfigError('Decision brief settings could not be loaded. Existing recommendations remain available.');
      }
    );
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!customerId) return undefined;
    setInsight(null);
    setInsightError('');
    setRecs([]);
    setLoading(true);
    const unsubInsight = onSnapshot(
      doc(db, collectionPath('customerInsights'), customerId),
      snap => { setInsight(snap.exists() ? snap.data() : null); setInsightError(''); setLoading(false); },
      err => {
        console.error('Failed to load customer insights:', err);
        setInsight(null);
        setInsightError('Customer insights and AI summary could not be loaded. Check your recommendation permissions or try again later.');
        setLoading(false);
      }
    );
    let unsubRecs = () => {};
    if (canReadTeam || repNames.length > 0) {
      const recommendations = collection(db, collectionPath('recommendations'));
      const recQuery = canReadTeam
        ? query(recommendations, where('customerId', '==', customerId))
        : query(
          recommendations,
          where('customerId', '==', customerId),
          where('salesRepNames', 'array-contains-any', repNames)
        );
      unsubRecs = onSnapshot(
        recQuery,
        snap => setRecs(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
        err => console.error('Failed to load recommendations:', err)
      );
    } else {
      setRecs([]);
    }
    return () => { unsubInsight(); unsubRecs(); };
  }, [canReadTeam, customerId, repNames]);

  const openRecs = useMemo(
    () => recs.filter(r => OPEN_STATUSES.includes(r.status)).sort((a, b) =>
      (effectiveStatus(a) === 'snoozed') - (effectiveStatus(b) === 'snoozed') || sortRecommendations(a, b)),
    [recs]
  );
  const historyRecs = useMemo(
    () => recs.filter(r => !OPEN_STATUSES.includes(r.status))
      .sort((a, b) => (b.updatedAt?.toMillis?.() || 0) - (a.updatedAt?.toMillis?.() || 0))
      .slice(0, 20),
    [recs]
  );
  const brief = briefEnabled ? buildDecisionBrief(customerId, insight, openRecs) : null;
  const listedRecs = openRecs.filter(rec => rec.id !== brief?.recommendation.id);

  useEffect(() => {
    if (!briefEnabled) return undefined;
    const now = Date.now();
    const changes = recs.flatMap(rec => [rec.expiresAt, rec.snoozeUntil])
      .map(value => value?.toDate?.().getTime()).filter(time => Number.isFinite(time) && time > now);
    if (!changes.length) return undefined;
    const timer = setTimeout(() => setBriefClock(value => value + 1),
      Math.min(Math.min(...changes) - now, 2147483647));
    return () => clearTimeout(timer);
  }, [briefEnabled, recs, briefClock]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await refreshCustomerRecommendations(customerId);
      const rulesMessage = `Recommendations refreshed — ${res.created} new, ${res.updated} updated`;
      const llmMessage = aiSummaryStatusMessage(res.llmStatus, res.llmErrorCode);
      onToast?.({ message: rulesMessage + llmMessage, type: 'success' });
    } catch (err) {
      onToast?.({ message: err.message || 'Refresh failed', type: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading recommendations…</div>;

  const s = insight?.signals;
  const band = insight?.healthBand;

  return (
    <div className="p-4 space-y-4">
      {modals}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Recommendations</h3>
          <p className="text-xs text-gray-500">
            Suggestions only — you decide what to act on.
            {insight?.generatedAt?.toDate && ` Last refreshed ${insight.generatedAt.toDate().toLocaleString()} (${insight.generatedBy === 'nightly' ? 'nightly' : 'on demand'}).`}
          </p>
        </div>
        {canGenerate && (
          <Tooltip text="Recalculate insights and recommendations from the latest data">
            <button type="button" onClick={handleRefresh} disabled={refreshing} className="px-3 py-1.5 text-sm rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50">
              {refreshing ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </Tooltip>
        )}
      </div>

      {briefConfigError && <p role="alert" className="text-sm text-amber-800">{briefConfigError}</p>}
      {insightError && <p role="alert" className="text-sm text-amber-800">{insightError}</p>}
      {brief && <DecisionBrief key={`${customerId}-${brief.recommendation.id}-${brief.inputHash}`}
        brief={brief} canAct={canAct} busy={busyId === brief.recommendation.id}
        handlers={handlers} onViewTab={onViewTab} onToast={onToast} />}

      {!insight ? (
        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-600">
          No insights yet for this customer. {canGenerate ? 'Click Refresh to generate them.' : 'They will appear after the nightly refresh.'}
        </div>
      ) : (
        <div className="space-y-3">
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${HEALTH_BAND_META[band] || 'bg-gray-100 text-gray-700'}`}>{band}</span>
            {typeof insight.healthScore === 'number' && (
              <Tooltip text={insight.healthBreakdown ? `Recency ${insight.healthBreakdown.recency}/40 · Trend ${insight.healthBreakdown.trend}/25 · Touch ${insight.healthBreakdown.touch}/20 · Hygiene ${insight.healthBreakdown.hygiene}/15` : 'Health score'}>
                <span className="text-sm text-gray-700">Health score <strong>{insight.healthScore}</strong>/100</span>
              </Tooltip>
            )}
            {insight.healthBandPrev && insight.healthBandPrev !== band && (
              <span className="text-xs text-gray-500">was {insight.healthBandPrev}</span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
              label="Last order"
              value={s.orders.lastOrderDate || 'Never'}
              hint={s.orders.daysSinceLastOrder !== null ? `${s.orders.daysSinceLastOrder} days ago` : null}
            />
            <Stat
              label="Typical reorder cycle"
              value={s.orders.typicalDaysBetweenOrders ? `~${s.orders.typicalDaysBetweenOrders} days` : '—'}
              hint={s.orders.expectedNextOrderDate ? `Next expected ${s.orders.expectedNextOrderDate}` : null}
            />
            <Stat
              label="Item revenue (12 mo)"
              value={money(s.orders.revenueLast12m)}
              hint={s.orders.yoyChangePct !== null ? `${s.orders.yoyChangePct >= 0 ? '+' : ''}${s.orders.yoyChangePct}% vs prior 12 mo` : `Prior 12 mo: ${money(s.orders.revenuePrev12m)}`}
            />
            <Stat
              label="Last touch"
              value={dateOnly(s.activity.lastTouchAt)}
              hint={`${s.activity.openTaskCount} open tasks${s.activity.overdueTaskCount ? `, ${s.activity.overdueTaskCount} overdue` : ''}`}
            />
            <Stat label="Open opportunities" value={s.pipeline.openCount} hint={s.pipeline.stale.length ? `${s.pipeline.stale.length} stale` : null} />
            <Stat label="Email engagement (90 d)" value={`${s.engagement.opens} opens · ${s.engagement.clicks} clicks`} hint={s.engagement.unsubscribed ? 'Unsubscribed' : null} />
            <Stat label="Contacts" value={s.coverage.contactCount} hint={s.coverage.hasPrimary ? 'Primary set' : 'No primary'} />
            <Stat
              label="Buying months"
              value={s.orders.seasonal.recurringMonths.length ? s.orders.seasonal.recurringMonths.join(', ') : '—'}
            />
          </div>
          {s.orders.topStyles?.length > 0 && (
            <div className="mt-4">
              <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">Top styles (24 mo)</h4>
              <ol className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {s.orders.topStyles.slice(0, 3).map((style, index) => (
                  <li key={`${style.styleNumber}-${index}`} className="min-w-0 rounded border border-gray-200 bg-white p-2 text-sm">
                    <p className="truncate font-medium text-gray-900" title={style.name}>
                      {index + 1}. {style.name && style.name !== style.styleNumber ? style.name : 'Unnamed style'}
                    </p>
                    <p className="text-xs text-gray-600">SKU {style.styleNumber}</p>
                    <p className="text-xs text-gray-500">
                      {money(style.revenue24m)}{style.lastOrdered ? ` · last ordered ${style.lastOrdered}` : ''}
                    </p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
        {insight.llmStatus === 'schema_rejected' && (
          <div role="status" className="border border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-800">
            The AI summary for this customer failed its format or evidence checks, so it was not shown.
            Refreshing again won’t retry it until the customer data changes or tomorrow.
            The rules-based recommendations below are unaffected.
          </div>
        )}
        {insight.llmStatus === 'ok' && (insight.summary || insight.talkingPoints?.length > 0) && (
          <section className="border border-indigo-100 rounded-lg p-4 bg-white" aria-label="AI-assisted customer summary">
            <h4 className="text-sm font-semibold text-gray-900">AI-assisted summary</h4>
            {insight.summary && <p className="mt-1 text-sm text-gray-700">{insight.summary}</p>}
            <div className="mt-2"><EvidenceReferences refs={insight.summaryEvidenceRefs} /></div>
            {Array.isArray(insight.talkingPoints) && insight.talkingPoints.length > 0 && (
              <ul className="mt-3 space-y-2">
                {insight.talkingPoints.map((point, index) => (
                  <li key={`talking-point-${index}`} className="text-sm text-gray-700">
                    <p>{point.text}</p>
                    <EvidenceReferences refs={point.evidenceRefs} />
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
        </div>
      )}

      <div>
        {openRecs.length === 0 && insight && (
          <div className="text-sm text-gray-600">No open recommendations — nice work. 🎉</div>
        )}
        {listedRecs.length > 0 && (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-visible">
            {(showAllOpen ? listedRecs : listedRecs.slice(0, PANEL_PREVIEW)).map(rec => (
              <RecommendationCard key={rec.id} rec={rec} canAct={canAct} busy={busyId === rec.id} onViewTab={onViewTab} compact {...handlers} />
            ))}
          </div>
        )}
        {listedRecs.length > PANEL_PREVIEW && (
          <button type="button" onClick={() => setShowAllOpen(v => !v)} className="text-sm text-indigo-600 hover:underline mt-2">
            {showAllOpen ? 'Show fewer' : `Show all ${listedRecs.length} recommendations`}
          </button>
        )}
      </div>

      {historyRecs.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowHistory(v => !v)} className="text-sm text-indigo-600 hover:underline">
            {showHistory ? 'Hide history' : `Show history (${historyRecs.length})`}
          </button>
          {showHistory && (
            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 mt-3 opacity-80">
              {historyRecs.map(rec => <RecommendationCard key={rec.id} rec={rec} canAct={false} compact {...handlers} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecommendationsPanel;
